import { Flex } from "antd";
import type { ReactNode } from "react";
import { StatusBadge } from "pages/schedule/component/ScheduleStatusBadge";
import { translate, translateWithVars } from "shared/languages";
import { colors } from "shared/theme/color";
import { CustomerPackage } from "shared/types";
import { creditCostSuffix, getActualRemainingCredits, packageActivation, PRODUCT_TYPE_LABELS } from "shared/utils";
import { formatDate } from "shared/utils/dayjs";
import { textPx } from "shared/constants/zoom";

// Selectable package list — one radio card per package (name + remaining-credit
// count), bound to a Form.Item via the injected value/onChange. Replaces the
// package <Select> with the prototype's list-item cards; selection logic is the
// same (the field value is the chosen package id). With no packages, renders
// the caller's empty notice in the control slot so it sits snug under the
// field label (an empty list would leave the control's min-height as a gap).
export const PackageRadioList = ({
  value,
  onChange,
  packages,
  emptyNotice,
}: {
  value?: string;
  onChange?: (id: string) => void;
  packages: CustomerPackage[];
  emptyNotice?: ReactNode;
}) => {
  if (packages.length === 0) return <>{emptyNotice}</>;

  return (
    <Flex vertical gap={8}>
      {packages.map((cp) => {
        const selected = value === cp.id;
        return (
          <button
            key={cp.id}
            type="button"
            onClick={() => onChange?.(cp.id)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 11,
              width: "100%",
              padding: "11px 12px",
              borderRadius: 12,
              background: selected ? colors.bgSoft : colors.card,
              border: `1px solid ${selected ? colors.ink : colors.lineInput}`,
              textAlign: "left",
              cursor: "pointer",
              transition: "border-color 150ms ease, background 150ms ease",
            }}
          >
            <span
              style={{
                width: 16,
                height: 16,
                borderRadius: "50%",
                border: `1.5px solid ${selected ? colors.ink : colors.ink3}`,
                position: "relative",
                flexShrink: 0,
              }}
            >
              {selected && (
                <span
                  style={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    background: colors.ink,
                  }}
                />
              )}
            </span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 600, fontSize: textPx(13), color: colors.ink }}>
                {cp.package_deal.name}
              </div>
              {cp.expiry_date && (
                <div
                  style={{ fontSize: textPx(11.5), color: colors.ink3, marginTop: 2 }}
                >
                  {packageActivation(cp).unactivated
                    ? translateWithVars("label.valid_from_first_class", {
                        n: packageActivation(cp).durationLabel,
                      })
                    : `${translate("label.expiry_date")}: ${formatDate(
                        cp.expiry_date,
                        "DD MMM YYYY",
                      )}`}
                </div>
              )}
            </div>
            <div style={{ marginLeft: "auto", whiteSpace: "nowrap" }}>
              {cp.package_deal.unlimited ? (
                <StatusBadge tone="navy" label={PRODUCT_TYPE_LABELS.membership} dot={false} />
              ) : (
                <span
                  style={{
                    fontSize: textPx(12),
                    fontWeight: 600,
                    color: colors.ink2,
                  }}
                  title={translate("customer_package.remaining")}
                >
                  {`${getActualRemainingCredits(cp)}/${cp.total_credits}${creditCostSuffix(cp)}`}
                </span>
              )}
            </div>
          </button>
        );
      })}
    </Flex>
  );
};
